import { c as _c } from "react/compiler-runtime";
import { Box, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import React from 'react';
import { LuCircleCheck } from 'react-icons/lu';
export function formatCompactRaise(amount?: number | null): string {
  if (!amount || amount <= 0) {
    return '$500k';
  }
  if (amount >= 1_000_000) {
    const millions = amount / 1_000_000;
    return `$${millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(1)}M`;
  }
  return `$${Math.round(amount / 1_000)}k`;
}
export const CheckRow: React.FC<{
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(4);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Icon asChild={true} boxSize={4} color="orange.fg" flexShrink={0} marginTop="2px"><LuCircleCheck strokeWidth={2.5} /></Icon>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["sm", "md"];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== children) {
    t3 = <HStack align="flex-start" color="fg" gap={2.5}>{t1}<Text color="fg" fontSize={t2} fontWeight="medium">{children}</Text></HStack>;
    $[2] = children;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
};
export const ProofStats: React.FC = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <HStack align="stretch" alignSelf="stretch" bg="bg.subtle" borderColor="border" borderRadius="lg" borderWidth="1px" gap={0} overflow="hidden" width="full">{[{
        n: "22,421",
        l: "Investors"
      }, {
        n: "$20B",
        l: "Raised"
      }].map(_temp)}</HStack>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export const AccentHeading: React.FC<{
  line1: string;
  accent: string;
}> = t0 => {
  const $ = _c(6);
  const {
    line1,
    accent
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["xl", "2xl", "3xl"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== accent) {
    t2 = <Box as="span" color="colorPalette.fg" colorPalette="orange">{accent}</Box>;
    $[1] = accent;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== line1 || $[4] !== t2) {
    t3 = <Text as="h3" fontSize={t1} fontWeight="semibold" letterSpacing="tight" lineHeight="shorter" textAlign="start">{line1}{" "}{t2}</Text>;
    $[3] = line1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};
export const WhyWeAsk: React.FC<{
  title?: string;
  items: React.ReactNode[];
}> = t0 => {
  const $ = _c(9);
  const {
    title: t1,
    items
  } = t0;
  const title = t1 === undefined ? "Why we ask" : t1;
  let t2;
  if ($[0] !== title) {
    t2 = <Text color="fg.subtle" fontSize="sm" fontWeight="bold" letterSpacing="0.1em" textAlign="start" textTransform="uppercase">{title}</Text>;
    $[0] = title;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== items) {
    t3 = items.map(_temp2);
    $[2] = items;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <VStack align="stretch" gap={2}>{t3}</VStack>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t2 || $[7] !== t4) {
    t5 = <VStack align="stretch" alignSelf="stretch" gap={4}>{t2}{t4}</VStack>;
    $[6] = t2;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
};
export const ChoiceChip: React.FC<{
  active?: boolean;
  children: React.ReactNode;
  onClick?: () => void;
}> = t0 => {
  const $ = _c(7);
  const {
    active,
    children,
    onClick
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      borderColor: "colorPalette.solid"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = active ? "colorPalette.subtle" : "bg.muted";
  const t3 = active ? "colorPalette.solid" : "border.emphasized";
  const t4 = active ? "colorPalette.fg" : "fg";
  let t5;
  if ($[1] !== children || $[2] !== onClick || $[3] !== t2 || $[4] !== t3 || $[5] !== t4) {
    t5 = <Box _hover={t1} alignItems="center" as="button" bg={t2} borderColor={t3} borderRadius="full" borderWidth="1px" color={t4} colorPalette="orange" cursor="pointer" display="inline-flex" fontSize="sm" fontWeight="medium" gap={2} px={3.5} py={2} transition="all 0.15s" onClick={onClick}>{children}</Box>;
    $[1] = children;
    $[2] = onClick;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
};

/** Right-column card for form steps — left-aligned fields. */
export const Panel: React.FC<{
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [4, 5];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <VStack align="stretch" bgColor="colorPalette.card/50" borderColor="colorPalette.border" borderRadius="xl" borderWidth="1px" flex={1} gap={4} justify="center" padding={t1} textAlign="start">{children}</VStack>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
export const FieldGroup: React.FC<{
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <VStack align="stretch" gap={1.5} justify="flex-start" width="full">{children}</VStack>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export const FieldLabel: React.FC<{
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <Text color="fg" fontSize="md" fontWeight="semibold" textAlign="start" width="full">{children}</Text>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
function _temp(stat) {
  return <VStack key={stat.l} _last={{
    borderRightWidth: 0
  }} borderColor="border" borderRightWidth="1px" gap={0} paddingX={5} paddingY={2.5} width="full"><Text fontSize="2xl" fontWeight="extrabold" letterSpacing="tight">{stat.n}</Text><Text color="fg.muted" fontSize="xs" fontWeight="bold" letterSpacing="wider" textTransform="uppercase">{stat.l}</Text></VStack>;
}
function _temp2(item, i) {
  return <CheckRow key={i}>{item}</CheckRow>;
}